import { type ReactElement, type ReactNode, useState } from "react";

import {
	ActivityItem,
	type ActivityItemProps,
} from "@/components/Molecules/ActivityItem/ActivityItem";
import { cx } from "@/utils/cx";

export interface ActivityListItem
	extends Omit<ActivityItemProps, "showConnector" | "className" | "children"> {
	/** Stable identity for keys and exclusive-expand state. */
	id: string;
	/** Content shown inside the accordion when expanded. */
	content?: ReactNode;
}

export interface ActivityListProps {
	/** Chronological activity rows (most recent first). */
	items: ActivityListItem[];
	/**
	 * When true, only one item may be expanded at a time.
	 * @default false
	 */
	exclusiveExpand?: boolean;
	/** Accessible name for the list. */
	"aria-label"?: string;
	className?: string;
	/** Optional test id override. */
	"data-testid"?: string;
}

/**
 * Chronological timeline feed that stacks ActivityItem rows with dashed
 * connectors between them (omitted on the last row).
 *
 * @see Figma ActivityList 4376:25102
 */
export function ActivityList({
	items,
	exclusiveExpand = false,
	"aria-label": ariaLabel = "Activity",
	className,
	"data-testid": dataTestId = "activity-list",
}: ActivityListProps): ReactElement {
	const [openId, setOpenId] = useState<string | null>(() => {
		if (!exclusiveExpand) return null;
		const initiallyOpen = items.find((item) => item.defaultExpanded);
		return initiallyOpen?.id ?? null;
	});

	return (
		<ul
			aria-label={ariaLabel}
			className={cx("m-0 flex list-none flex-col gap-2 p-0", className)}
			data-testid={dataTestId}
		>
			{items.map((item, index) => {
				const {
					id,
					content,
					defaultExpanded,
					isExpanded: controlledExpanded,
					onExpandedChange,
					...itemProps
				} = item;
				const isLast = index === items.length - 1;

				const isExpanded = exclusiveExpand
					? (controlledExpanded ?? openId === id)
					: controlledExpanded;

				return (
					<li key={id} className="w-full">
						<ActivityItem
							{...itemProps}
							showConnector={!isLast}
							isExpanded={isExpanded}
							defaultExpanded={exclusiveExpand ? undefined : defaultExpanded}
							onExpandedChange={(expanded) => {
								if (exclusiveExpand) {
									setOpenId(expanded ? id : null);
								}
								onExpandedChange?.(expanded);
							}}
						>
							{content}
						</ActivityItem>
					</li>
				);
			})}
		</ul>
	);
}
